import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Camera, Crosshair, X, CheckCircle2, Video, AlertTriangle, CloudOff, RefreshCw } from "lucide-react";
import { queueVisit, listQueued, flushQueue } from "@/lib/offlineQueue";

const FLAGGED = ["Encroachment suspected", "Boundary damaged", "Dispute notice found"];
const OBSERVATIONS = ["Normal", "Encroachment suspected", "Boundary damaged",
  "Crop / Construction activity", "Dispute notice found", "Other"];

export default function Capture() {
  const { id } = useParams();
  const nav = useNavigate();
  const [parcel, setParcel] = useState(null);
  const [files, setFiles] = useState([]);
  const [geo, setGeo] = useState(null);
  const [geoState, setGeoState] = useState("idle");
  const [notes, setNotes] = useState("");
  const [observation, setObservation] = useState("Normal");
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refreshPending = () => listQueued().then((l) => setPending(l.length));

  const sync = async () => {
    if (!navigator.onLine) return;
    setSyncing(true);
    let result = { sent: 0, failed: 0 };
    try {
      result = await flushQueue(api);
    } catch (e) {
      /* stays queued */
    }
    const { sent, failed } = result;
    await refreshPending();
    setSyncing(false);
    if (sent) toast.success(`${sent} offline visit(s) uploaded`);
    if (failed) toast.error(`${failed} visit(s) still pending — will retry`);
  };

  useEffect(() => {
    refreshPending();
    const up = () => { setOnline(true); sync(); };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    if (navigator.onLine) sync();
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get(`/parcels/${id}`).then(({ data }) => setParcel(data)).catch(() => setParcel(false));
  }, [id]);
  const locate = () => {
    if (!navigator.geolocation) return setGeoState("unsupported");
    setGeoState("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeo({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGeoState("ok");
      },
      () => setGeoState("denied"),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  useEffect(() => { locate(); }, []);

  const addFiles = (e) => {
    setFiles((prev) => [...prev, ...Array.from(e.target.files)]);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (files.length === 0) return toast.error("Capture at least one photo or video");
    setBusy(true);

    const saveOffline = async (reason) => {
      await queueVisit({
        parcel_id: id,
        files: files.map((f) => ({ blob: f, name: f.name || "capture.jpg" })),
        latitude: geo?.latitude ?? null,
        longitude: geo?.longitude ?? null,
        accuracy: geo?.accuracy ?? null,
        notes,
        observation,
        captured_at: new Date().toISOString(),
      });
      await refreshPending();
      toast.success("Saved on this device", { description: reason });
      nav(`/parcels/${id}`);
    };

    if (!navigator.onLine) {
      await saveOffline("No signal — it will upload automatically once you are back online.");
      setBusy(false);
      return;
    }

    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    if (geo) {
      fd.append("latitude", geo.latitude);
      fd.append("longitude", geo.longitude);
      fd.append("accuracy", geo.accuracy);
    }
    fd.append("notes", notes);
    fd.append("observation", observation);
    fd.append("captured_at_client", new Date().toISOString());
    try {
      await api.post(`/parcels/${id}/visits`, fd);
      toast.success("Field visit recorded");
      nav(`/parcels/${id}`);
    } catch (err) {
      if (!err.response) await saveOffline("Upload failed — queued and will retry automatically.");
      else toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (parcel === false) return <div className="p-10 text-sm text-muted-foreground">Asset not accessible.</div>;
  if (!parcel) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-2xl mx-auto px-5 py-8 md:py-12 rise" data-testid="capture-page">
      <Link to={`/parcels/${id}`} data-testid="capture-back"
        className="inline-flex items-center gap-2 label-tiny hover:text-primary transition-colors duration-200">
        <ArrowLeft size={13} /> {parcel.name}
      </Link>

      <p className="label-tiny mt-6">Field capture</p>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tighter">Record site update</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Photos and videos are stamped with your location and the exact capture time.
      </p>

      {(!online || pending > 0) && (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
          data-testid="offline-banner">
          <CloudOff size={17} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">
              {online ? "Pending uploads" : "You are offline"}
            </p>
            <p className="mt-1 text-xs text-amber-800" data-testid="offline-status">
              {online
                ? `${pending} visit(s) saved on this device are waiting to upload.`
                : "Capture as usual — the visit is saved on this device and uploads automatically when you get signal."}
            </p>
          </div>
          {pending > 0 && online && (
            <Button type="button" variant="outline" size="sm" onClick={sync} disabled={syncing}
              data-testid="sync-now-button" className="rounded-lg shrink-0">
              <RefreshCw size={13} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Uploading…" : "Upload now"}
            </Button>
          )}
        </div>
      )}

      <div className="mt-6 surface p-5" data-testid="geo-card">        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="label-tiny flex items-center gap-1.5"><Crosshair size={13} /> Location</p>
            {geoState === "ok" ? (
              <p className="mt-2 text-sm font-mono" data-testid="geo-coords">
                {geo.latitude.toFixed(6)}, {geo.longitude.toFixed(6)}
                <span className="text-muted-foreground"> ±{Math.round(geo.accuracy)}m</span>
              </p>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground" data-testid="geo-status">
                {geoState === "locating" && "Acquiring GPS fix…"}
                {geoState === "denied" && "Location permission denied — visit will be saved without a geotag."}
                {geoState === "unsupported" && "This device does not support geolocation."}
                {geoState === "idle" && "Waiting for GPS…"}
              </p>
            )}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={locate} data-testid="geo-retry-button" className="rounded-lg shrink-0">
            Refresh
          </Button>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 space-y-6" data-testid="capture-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="rounded-xl border-2 border-dashed border-border bg-card p-6 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary transition-colors duration-200">
            <Camera size={24} className="text-primary" />
            <span className="text-sm font-semibold">Capture photo</span>
            <span className="text-xs text-muted-foreground">Camera or gallery</span>
            <input type="file" accept="image/*" capture="environment" multiple hidden onChange={addFiles} data-testid="capture-photo-input" />
          </label>
          <label className="rounded-xl border-2 border-dashed border-border bg-card p-6 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary transition-colors duration-200">
            <Video size={24} className="text-primary" />
            <span className="text-sm font-semibold">Capture video</span>
            <span className="text-xs text-muted-foreground">Short site walkthrough</span>
            <input type="file" accept="video/*" capture="environment" multiple hidden onChange={addFiles} data-testid="capture-video-input" />
          </label>
        </div>

        {files.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="capture-previews">
            {files.map((f, i) => (
              <div key={i} className="relative rounded-lg border border-border overflow-hidden bg-muted" data-testid={`capture-preview-${i}`}>
                {f.type.startsWith("image") ? (
                  <img src={URL.createObjectURL(f)} alt={f.name} className="aspect-square w-full object-cover" />
                ) : (
                  <div className="aspect-square flex items-center justify-center bg-zinc-900 text-white"><Video size={20} /></div>
                )}
                <button type="button" data-testid={`remove-preview-${i}`}
                  onClick={() => setFiles(files.filter((_, x) => x !== i))}
                  className="absolute top-1 right-1 rounded-md bg-black/70 text-white p-1 hover:bg-destructive transition-colors duration-200">
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div>
          <Label className="label-tiny">Site observation</Label>
          <Select value={observation} onValueChange={setObservation}>
            <SelectTrigger className="mt-2 h-11" data-testid="observation-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {OBSERVATIONS.map((o) => <SelectItem key={o} value={o} data-testid={`observation-${o}`}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
          {FLAGGED.includes(observation) && (
            <p className="mt-2 text-xs text-destructive flex items-center gap-1.5 font-medium" data-testid="escalation-notice">
              <AlertTriangle size={13} /> Admins will be emailed immediately with your photos and geotag.
            </p>
          )}
        </div>

        <div>
          <Label className="label-tiny">Notes</Label>
          <Textarea rows={4} className="mt-2" data-testid="capture-notes-input" value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Fencing intact on the east boundary. Neighbouring plot under construction." />
        </div>

        <Button type="submit" disabled={busy} data-testid="capture-submit-button"
          className="w-full h-14 rounded-xl text-base active:scale-[0.98] transition-transform duration-150">
          <CheckCircle2 size={18} className="mr-2" />
          {busy ? "Uploading capture…" : online ? "Submit field visit" : "Save on device"}
        </Button>
      </form>
    </div>
  );
}
